import { ModelDataType } from 'src/app/interfaces/model-data-type';

import { Address } from './address';
import { BaseModel } from './base-model';
import { Contact } from './contact';
import { OwnerFinding } from './owner-finding';
import { Vendor } from './vendor';

export class Owner extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static OwnerProperties: (
    | {
        property: keyof Owner;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof Owner;
        type: 'alias';
      }
    | {
        property: keyof Owner;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    { property: 'email', type: 'column', dataType: 'string' },
    { property: 'firstName', type: 'column', dataType: 'string' },
    { property: 'lastName', type: 'column', dataType: 'string' },
    { property: 'phone', type: 'column', dataType: 'string' },
    { property: 'percentOwner', type: 'column', dataType: 'number' },
    { property: 'mobile', type: 'column', dataType: 'string' },
    { property: 'title', type: 'column', dataType: 'string' },
    { property: 'dob', type: 'column', dataType: 'date' },
    { property: 'ssn', type: 'column', dataType: 'string' },
    { property: 'tin', type: 'column', dataType: 'string' },
    { property: 'LexisNexisId', type: 'column', dataType: 'number' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },

    { property: 'lastRanOwnerVerificationReportDate', type: 'alias' },
    { property: 'hasSSN', type: 'alias' },

    { property: 'Vendor', model: 'Vendor', type: 'association' },
    { property: 'Contact', model: 'Contact', type: 'association' },
    { property: 'Address', model: 'Address', type: 'association' },
    { property: 'OwnerFinding', model: 'OwnerFinding', type: 'association' }
  ];

  static {
    Owner.OwnerProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        Owner.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        Owner.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        Owner.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(OwnerObject: Partial<Owner>) {
    super();

    Owner.OwnerProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = OwnerObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  VendorId?: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  percentOwner?: number;
  mobile?: string | null;
  title?: string | null;
  dob?: Date | string;
  ssn?: string | null;
  tin?: string | null;
  LexisNexisId?: string | null;
  createdAt?: Date;
  updatedAt?: Date;

  // Aliased Columns
  lastRanOwnerVerificationReportDate?: Date | null;
  hasSSN?: boolean;

  Vendor?: Vendor;
  Contact?: Contact;
  Address?: Address;
  OwnerFinding?: OwnerFinding;
}
